import React, { useState, useEffect } from 'react'
import {
  Section,
  Panel,
  Header,
  Icon,
  Button,
  ButtonLink,
} from '@lightspeed/design-system-react'
import { VendImage } from '@vend/react-utilities'
import { T, UT } from '@transifex/react'
import { useUserManager, useUserSwitchingService } from '../../ng-compat/hooks'
import { useAppSelector } from '../../state/hooks'
import { selectLoggedInUserId } from '../../state/slices/posState/posStateSlice'

interface Props {
  activeRegisterId: string
}

export const CashManagementDisabled: React.FC<Props> = ({
  activeRegisterId,
}) => {
  const userManager = useUserManager()
  const userSwitchingService = useUserSwitchingService()

  const [
    canEnableCashManagement,
    setCanEnableCashManagement,
  ] = useState<boolean>(userManager.hasAdminAccess())
  const [showRefresh, setShowRefresh] = useState<boolean>(false)

  // Get logged in userId from posState in redux store
  const userId = useAppSelector(selectLoggedInUserId)

  useEffect(() => {
    setCanEnableCashManagement(userManager.hasAdminAccess())
  }, [userId, userManager])

  return (
    <Section>
      <Panel className="wr-cash-management-panel">
        <Section
          category="panel"
          className="vd-align-center wr-cash-management-panel-header"
        >
          <VendImage
            className="wr-cash-management-panel-image"
            relativeImagePath="sell/cash-management-onboarding-v2.svg"
          />
          <Header level="1" type="hero">
            <T _str="Introducing Cash Management" _context="Page Header" />
          </Header>
          <p className="vd-p vd-mb0">
            <T
              _str="Start tracking cash movements across shifts, days and cashiers, and take control of your register."
              _context="Page description"
            />
          </p>
          {canEnableCashManagement ? (
            <div>
              <ButtonLink
                href={`/register/${activeRegisterId}/edit`}
                onClick={() => setShowRefresh(true)}
                target="_blank"
                className="vd-mt5"
              >
                <T _str="Enable Cash Management" _context="Button" />
              </ButtonLink>
              {showRefresh && (
                <p className="vd-p vd-text--negative vd-mt5">
                  <Icon icon="fa-exclamation-triangle" className="vd-mr1" />{' '}
                  <T _str="Please manually refresh this page after enabling Cash Management." />
                </p>
              )}
            </div>
          ) : (
            <div>
              <p className="vd-p vd-text--negative vd-mt5">
                <T _str="Please switch to an admin to enable Cash Management." />
              </p>
              <Button onClick={() => userSwitchingService.selectUser()}>
                <T _str="Switch users" _context="Button" />
              </Button>
            </div>
          )}
        </Section>
        <Section category="panel">
          <div className="vd-flex vd-flex-justify--center">
            <ul className="wr-cash-management-value-props vd-mt5 vd-mb5">
              <li className="vd-pl10">
                <Icon
                  className="wr-cash-management-value-prop-icon"
                  icon="fa-exchange"
                ></Icon>
                <Header level="3" type="sub-heading" className="vd-mb1">
                  <T
                    _str="Track petty cash in and out, plus safe deposit drops"
                    _context="Sub Header"
                  />
                </Header>
                <p className="vd-p">
                  <T
                    _str="Need to buy supplies? Want to make a cash deposit at the bank? Cash Management records the money you take out of your cash drawer so you know exactly where your cash is. You can even make notes to remind yourself. When you close your register each day, Lightspeed Retail will automatically adjust the amount."
                    _context="Sub header description"
                  />
                </p>
              </li>
              <li className="vd-pl10 vd-mt5">
                <Icon
                  className="wr-cash-management-value-prop-icon"
                  icon="fa-user-secret"
                ></Icon>
                <Header level="3" type="sub-heading" className="vd-mb1">
                  <T
                    _str="Check for discrepancies and reduce theft"
                    _context="Sub Header"
                  />
                </Header>
                <p className="vd-p">
                  <T
                    _str="With visibility over all your cash movements throughout the day, it’s easier to track discrepancies and reduce theft."
                    _context="Sub header description"
                  />
                </p>
              </li>
              <li className="vd-pl10 vd-mt5">
                <Icon
                  className="wr-cash-management-value-prop-icon"
                  icon="fa-calculator"
                ></Icon>
                <Header level="3" type="sub-heading" className="vd-mb1">
                  <T
                    _str="Keep your accounting in check"
                    _context="Sub Header"
                  />
                </Header>
                <p className="vd-p">
                  <T
                    _str="Use Xero? Every cash movement will also be sent to Xero. Your accountant will love you."
                    _context="Sub header description"
                  />
                </p>
              </li>
            </ul>
          </div>
        </Section>
        <Section className="vd-align-center">
          <p className="vd-p">
            <UT
              _inline
              _str={`Find out more about Lightspeed Retail Cash Management in our <a class="vd-link" href="https://support.vendhq.com/hc/en-us/articles/207884598-Cash-Management-in-Vend" target="_blank">Help Center <i class="fa fa-external-link vd-ml1 vd-icon"></i></a>`}
            />
          </p>
        </Section>
      </Panel>
    </Section>
  )
}
